package fyi.moko.hotpl.ui.photo

import kotlin.math.max
import kotlin.math.min

/**
 * The zoom/pan state of one photo in the full-screen viewer, kept out of the UI so the
 * arithmetic can be tested without a device.
 *
 * 좌표계: [offsetX]/[offsetY]는 **뷰포트 중심 기준**으로 사진 중심이 얼마나 밀렸는지(px)다.
 * 화면 좌상단 기준이 아니다 — 확대·축소가 중심을 기준으로 일어나므로 중심 기준이 식을
 * 짧게 만든다. 같은 이유로 포커스 좌표(핀치 중심, 더블탭 지점)도 중심 기준으로 받는다.
 */
data class ZoomPan(
    val scale: Float = MIN_SCALE,
    val offsetX: Float = 0f,
    val offsetY: Float = 0f,
) {
    /** 1×이면 pager가 수평 드래그를 가져간다 — 확대 중에만 팬이 의미를 갖는다. */
    val isZoomed: Boolean get() = scale > MIN_SCALE + ZOOM_EPSILON

    companion object {
        const val MIN_SCALE = 1f
        const val MAX_SCALE = 4f
        /** 더블탭 한 번으로 가는 배율. 4×는 한 번에 가기엔 너무 멀고 되돌아오기도 번거롭다. */
        const val DOUBLE_TAP_SCALE = 2.5f
        private const val ZOOM_EPSILON = 0.01f
    }
}

/** The size a photo occupies at 1× — `ContentScale.Fit`, so the whole picture is inside. */
data class FittedSize(val width: Float, val height: Float)

/**
 * Fit 기준 크기: 사진 전체가 뷰포트 안에 들어오는 가장 큰 크기다. 이것이 뷰어의 1×이고,
 * 리뷰어가 요청한 "잘리지 않고 다 보이는" 상태다(R-9).
 */
fun fittedSize(
    imageWidth: Int,
    imageHeight: Int,
    viewportWidth: Int,
    viewportHeight: Int,
): FittedSize {
    if (imageWidth <= 0 || imageHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
        return FittedSize(0f, 0f)
    }
    val ratio = min(
        viewportWidth.toFloat() / imageWidth,
        viewportHeight.toFloat() / imageHeight,
    )
    return FittedSize(imageWidth * ratio, imageHeight * ratio)
}

/**
 * 사진의 가장자리가 뷰포트 안으로 끌려 들어오지 않도록 offset을 가둔다. 확대된 축은
 * 넘치는 만큼의 절반까지 밀 수 있고, 아직 다 들어오는 축은 가운데 고정이다 — 1×에서
 * 사진이 화면 중앙을 벗어나 떠 있으면 "놓친 것"처럼 보인다.
 */
fun ZoomPan.clampedTo(fitted: FittedSize, viewportWidth: Int, viewportHeight: Int): ZoomPan {
    val limitX = max(0f, (fitted.width * scale - viewportWidth) / 2f)
    val limitY = max(0f, (fitted.height * scale - viewportHeight) / 2f)
    return copy(
        offsetX = offsetX.coerceIn(-limitX, limitX),
        offsetY = offsetY.coerceIn(-limitY, limitY),
    )
}

/**
 * Pinch: scales around [focusX]/[focusY] (viewport-centre coordinates) so the point under
 * the fingers stays under the fingers.
 */
fun ZoomPan.zoomedBy(
    factor: Float,
    focusX: Float,
    focusY: Float,
    fitted: FittedSize,
    viewportWidth: Int,
    viewportHeight: Int,
): ZoomPan {
    val target = (scale * factor).coerceIn(ZoomPan.MIN_SCALE, ZoomPan.MAX_SCALE)
    if (target == scale) return clampedTo(fitted, viewportWidth, viewportHeight)
    val ratio = target / scale
    return copy(
        scale = target,
        offsetX = focusX - ratio * (focusX - offsetX),
        offsetY = focusY - ratio * (focusY - offsetY),
    ).clampedTo(fitted, viewportWidth, viewportHeight)
}

/** Drag: only meaningful while zoomed; the caller leaves 1× drags to the pager. */
fun ZoomPan.pannedBy(
    dx: Float,
    dy: Float,
    fitted: FittedSize,
    viewportWidth: Int,
    viewportHeight: Int,
): ZoomPan = copy(offsetX = offsetX + dx, offsetY = offsetY + dy)
    .clampedTo(fitted, viewportWidth, viewportHeight)

/**
 * Double tap toggles: zoomed → back to the whole picture, 1× → in on the tapped point.
 * 돌아오는 쪽을 항상 정확히 1×·중앙으로 두는 건 의도다 — 애매하게 확대된 채로 남으면
 * "전체가 보이는 상태"로 가는 방법이 사라진다.
 */
fun ZoomPan.afterDoubleTap(
    focusX: Float,
    focusY: Float,
    fitted: FittedSize,
    viewportWidth: Int,
    viewportHeight: Int,
): ZoomPan = if (isZoomed) {
    ZoomPan()
} else {
    zoomedBy(
        factor = ZoomPan.DOUBLE_TAP_SCALE / scale,
        focusX = focusX,
        focusY = focusY,
        fitted = fitted,
        viewportWidth = viewportWidth,
        viewportHeight = viewportHeight,
    )
}
